import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { CONTACT, LEGAL } from "@/data/content";

const Shell = ({ title, updated, children }) => (
  <main className="min-h-screen bg-cream py-16 px-5 sm:px-8">
    <div className="max-w-3xl mx-auto">
      <Link to="/" data-testid="back-home" className="inline-flex items-center gap-2 text-strawberry font-semibold hover:underline">
        <ArrowLeft size={18} /> Retour à l'accueil
      </Link>
      <h1 className="mt-6 font-heading text-4xl sm:text-5xl font-medium text-chocolate">{title}</h1>
      {updated && <p className="mt-2 text-sm text-muted-foreground">Dernière mise à jour : {updated}</p>}
      <div className="mt-8 space-y-6 text-cocoa leading-relaxed">{children}</div>
    </div>
  </main>
);

const Block = ({ h, children }) => (
  <section>
    <h2 className="font-heading text-2xl font-semibold text-chocolate mb-2">{h}</h2>
    <div className="space-y-2">{children}</div>
  </section>
);

const Row = ({ label, value }) => (
  <p><span className="font-semibold text-chocolate">{label} :</span> {value}</p>
);

export const MentionsLegales = () => (
  <Shell title="Mentions légales" updated="Juin 2026">
    <Block h="Éditeur du site">
      <Row label="Nom commercial" value={LEGAL.nomCommercial} />
      <Row label="Identité de l'exploitant" value={LEGAL.responsable} />
      <Row label="Statut" value={LEGAL.statut} />
      <Row label="Téléphone" value={LEGAL.telephone} />
      <Row label="E-mail" value={LEGAL.email} />
      <Row label="Numéro SIRET" value={LEGAL.siret} />
      <Row label="Ville d'immatriculation" value={LEGAL.villeImmatriculation} />
      <Row label="TVA intracommunautaire" value={LEGAL.tva} />
    </Block>

    <Block h="Directeur de la publication">
      <p>{LEGAL.responsable}</p>
    </Block>

    <Block h="Hébergement">
      <Row label="Hébergeur" value={LEGAL.hebergeur.nom} />
      <Row label="Adresse" value={LEGAL.hebergeur.adresse} />
      <Row label="Contact" value={LEGAL.hebergeur.tel} />
    </Block>

    <Block h="Activité et zone d'intervention">
      <p>{LEGAL.nomCommercial} propose des prestations de stand de glace à l'italienne pour tous types d'événements.</p>
      <Row label="Zones d'intervention" value={LEGAL.zones} />
      <Row label="Moyens de paiement acceptés" value={LEGAL.moyensPaiement} />
      <p>Contact : <a href={`mailto:${LEGAL.email}`} className="text-strawberry hover:underline">{LEGAL.email}</a></p>
    </Block>

    <Block h="Propriété intellectuelle">
      <p>L'ensemble des contenus (textes, logo, visuels) présents sur ce site est protégé. Toute reproduction, totale ou partielle, sans autorisation écrite est interdite. Certaines images d'illustration sont issues de banques d'images libres de droits.</p>
    </Block>

    <Block h="Responsabilité">
      <p>{LEGAL.nomCommercial} s'efforce d'assurer l'exactitude des informations diffusées sur ce site mais ne saurait être tenue responsable des erreurs ou omissions. Les prestations font l'objet d'un devis personnalisé et sans engagement.</p>
    </Block>
  </Shell>
);

export const Confidentialite = () => (
  <Shell title="Politique de confidentialité" updated="Juin 2026">
    <Block h="Responsable du traitement">
      <p>Les données personnelles collectées sur ce site sont traitées par <strong>{LEGAL.nomCommercial}</strong> ({LEGAL.statut}), joignable à l'adresse <a href={`mailto:${LEGAL.email}`} className="text-strawberry hover:underline">{LEGAL.email}</a> ou au {LEGAL.telephone}.</p>
    </Block>

    <Block h="Quelles données sont collectées ?">
      <p>Via nos formulaires (contact, demande de devis, demande de rappel), nous collectons uniquement les données que vous nous transmettez :</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>Nom et prénom</li>
        <li>Téléphone et adresse e-mail</li>
        <li>Informations sur votre événement (date, adresse, nombre de personnes, type d'événement, saveurs et options souhaitées)</li>
        <li>Le contenu de votre message</li>
      </ul>
    </Block>

    <Block h="Pourquoi ces données sont-elles collectées ?">
      <p>Vos données sont utilisées exclusivement pour :</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>traiter votre demande de devis ou de contact ;</li>
        <li>vous recontacter et établir une offre personnalisée ;</li>
        <li>assurer le suivi de la relation commerciale.</li>
      </ul>
      <p>Elles ne sont ni vendues, ni louées, ni cédées à des tiers.</p>
    </Block>

    <Block h="Combien de temps sont-elles conservées ?">
      <p>Vos données sont conservées pour la durée nécessaire au traitement de votre demande, puis pendant une durée maximale de 3 ans à compter de notre dernier contact à des fins de suivi commercial. Elles sont ensuite supprimées.</p>
    </Block>

    <Block h="Vos droits (RGPD)">
      <p>Conformément au Règlement Général sur la Protection des Données (RGPD), vous disposez des droits suivants sur vos données :</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>droit d'accès ;</li>
        <li>droit de rectification ;</li>
        <li>droit à l'effacement (suppression) ;</li>
        <li>droit d'opposition et de limitation du traitement ;</li>
        <li>droit à la portabilité de vos données.</li>
      </ul>
      <p>Pour exercer ces droits, contactez-nous à <a href={`mailto:${LEGAL.email}`} className="text-strawberry hover:underline">{LEGAL.email}</a>. Vous pouvez également introduire une réclamation auprès de la CNIL (www.cnil.fr).</p>
    </Block>

    <Block h="Cookies">
      <p>Ce site n'utilise pas de cookies publicitaires ni de traceurs de suivi à des fins marketing. Seuls des éléments techniques strictement nécessaires au bon fonctionnement du site peuvent être utilisés.</p>
    </Block>
  </Shell>
);
